// @flow
import React, { Fragment } from 'react'
import { multiPoint } from '@turf/helpers'
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader'
import GeoJSONWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter'
import { BufferOp } from 'jsts/org/locationtech/jts/operation/buffer'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import IconButton from '@material-ui/core/IconButton'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Snackbar from '@material-ui/core/Snackbar'
import { withStyles } from '@material-ui/core/styles'
import moment from 'moment'
import { GeoJSON } from 'ol/format'
import OlEvent from 'ol/events/Event'
import OlVectorLayer from 'ol/layer/Vector'
import OlVectorSource from 'ol/source/Vector'
import Fill from 'ol/style/Fill'
import Style from 'ol/style/Style'
import { olUtils } from 'catena/er2_ui'
import Er2Map from 'catena/er2_map/src/map'
import Color from 'catena/er2_map_userlayers/js/components/color'
import * as colors from '../styles/colors'
import Mapbrusher from './mapbrusher'
import type { FicusDataType } from '../reducers/select_framework_bar'
import type { TransimsType, ZoneType } from '../reducers/transims'
import stylesFicus from '../styles'
import type { SettingsBarType } from '../reducers/settings'

type Props = {
    classes?: {},
    ficusData: FicusDataType,
    er2Map: Er2Map,
    hasAnalysisPanel: boolean,
    hasHeader: boolean,
    onChangeProps: Function,
    onRun: Function,
    onSetZoneColor: Function,
    settings: SettingsBarType,
    theme: stylesFicus,
    tocVisible: boolean,
    transims: TransimsType,
    updateTransimsLayer: Function,
}

type State = {
    brushDataStr: string,
    // Zone that is currently being drawn
    currentZone: ZoneType,
    drawPromptRendered: boolean,
    drawType?: 'poly' | 'brush',
    helpDrawMethod: boolean,
    helpGeneral: boolean,
    hidden: {},
    startTime: string,
}

const baseStyles = {
    button: {
        width: 110,
        marginBottom: 10,
        marginLeft: 20,
        marginRight: 20,
        marginTop: 10,
        border: '1px solid transparent',
    },
    container: {
        marginLeft: 20,
    },
    radioGroup: {
        display: 'flex',
    },
    radioLabel: {
        padding: 2,
    },
    startTime: {
        marginBottom: 10,
        marginTop: 10,
        textAlign: 'center',
    },
    swatch: {
        display: 'inline-block',
        height: 20,
        margin: 10,
        width: 20,
        verticalAlign: 'middle',
    },
}

const styles = {
    ...baseStyles,
    buttonSelected: {
        ...baseStyles.button,
        border: '1px solid black',
    },
}

const opacity = 0.3

class Transims extends React.Component<Props, State> {
    static defaultProps = {
        classes: {},
    }

    state = {
        currentZone: '',
        drawType: 'poly',
        drawPromptRendered: true,
        helpDrawMethod: false,
        helpGeneral: false,
        hidden: {},
        startTime: moment(),
    }

    componentDidMount() {
        this.vectorSource = new OlVectorSource({
            projection: 'EPSG:3857',
        })
        this.vectorLayer = new OlVectorLayer({
            source: this.vectorSource,
            style: (feat, res) => this.getStyle(feat, res),
            zIndex: 1,
        })

        this.props.er2Map.olmap.addLayer(this.vectorLayer)
        this.props.er2Map.olmap.render()

        // Zoom to the transims template raster extent.
        if (this.props.transims.template_raster) {
            this.props.er2Map.setExtent(this.props.transims.template_raster.bbox_3857)
        }
    }

    componentWillUpdate(nextProps: Props, nextState: State, nextContext: any): void {
        if (this.props.transims.running && !nextProps.transims.running) {
            // Was running, now is finished.
            this.vectorSource.clear()
        }
        if (this.vectorLayer) {
            this.vectorLayer.setOpacity(this.props.settings.opacity)
        }
    }

    componentWillUnmount() {
        if (this.drawInteraction) {
            this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
        }
        this.props.er2Map.olmap.removeLayer(this.vectorLayer)
    }

    getStyle(feature) {
        const hidden = this.state.hidden
        const zone = feature.get('zone')
        if (!hidden[zone]) {
            const fill = new Fill({
                color: this.props.transims.zoneColors[zone],
            })
            return new Style({
                fill,
            })
        }
        return new Style({})
    }

    // Shapes that don't require buffers can be handled here.
    onDrawShape(evt: OlEvent) {
        evt.feature.set('zone', this.state.currentZone)
        this.vectorSource.addFeature(evt.feature)
    }

    onRun = () => {
        // Collect features and lines
        const features = this.vectorSource.getFeatures()
        let featuresGeojson = {
            type: 'FeatureCollection',
            features: [],
        }
        if (features.length) {
            featuresGeojson = JSON.parse(olUtils.writeFeatures(features))
        }
        this.props.onRun(featuresGeojson)
        this.setState({ startTime: moment() })
    }

    onBrushUpdated = () => {
        // Convert brush to polygon
        const brushDataStr = this.mapbrusher.canvas.getSaveData()
        const brushData = JSON.parse(brushDataStr)
        const { zoneColors } = this.props.transims
        const nfeatures = this.vectorSource.getFeatures().length
        // Rasterize the points
        brushData.lines.forEach((l) => {
            const points = []
            const zone = Object.entries(zoneColors).map(([k]) => k).filter(k => zoneColors[k] === l.brushColor)[0]
            let bufferRadius = 0
            l.points.forEach((p) => {
                // Get the edges of the brush
                const centroid = this.props.er2Map.olmap.getCoordinateFromPixel([p.x, p.y])
                if (!points.includes(centroid)) points.push(centroid)
                if (!bufferRadius) {
                    const top = this.props.er2Map.olmap.getCoordinateFromPixel([p.x - l.brushRadius, p.y])
                    bufferRadius = Math.abs(top[0] - centroid[0])
                }
            })
            // Create vector lines from point list.
            const mpTurf = multiPoint(points)
            const reader = new GeoJSONReader()
            const mp = reader.read(mpTurf.geometry)
            const buffered = BufferOp.bufferOp(mp, bufferRadius)
            const writer = new GeoJSONWriter()
            // Create feature
            const bufferedGeom = writer.write(buffered)
            const bufferedFeat = {
                type: 'Feature',
                geometry: bufferedGeom,
                properties: {
                    gid: nfeatures + 1,
                    zone,
                },
            }
            const olGeoJSON = new GeoJSON()
            const olFeat = olGeoJSON.readFeature(bufferedFeat)
            olFeat.setId(nfeatures + 1)
            this.vectorSource.addFeature(olFeat)
        })
        this.mapbrusher.canvas.clear()
    }

    onToggleVisible = zone => () => {
        const hidden = this.state.hidden
        hidden[zone] = !hidden[zone]
        this.setState({ hidden })
        this.vectorLayer.changed()
    }

    onTrash = zone => () => {
        // Remove all zone entries
        const features = []
        this.vectorSource.forEachFeature((f) => {
            if (f.get('zone') === zone) {
                features.push(f)
            }
        })
        // Just remove the last feature
        if (features.length) {
            this.vectorSource.removeFeature(features[features.length - 1])
            this.vectorLayer.changed()
        }
    }

    setCurrentZone = zone => () => {
        this.setDrawTypeAndZone(this.state.drawType, zone)
    }

    setDrawType = (e) => {
        this.setDrawTypeAndZone(e.target.value, this.state.currentZone)
    }

    setDrawTypeAndZone(drawType, currentZone) {
        if (this.state.drawType === drawType && this.state.currentZone === currentZone) {
            // Toggle off
            if (this.drawInteraction) {
                this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
                this.drawInteraction = null
            }
            this.setState({ currentZone: '', drawType, drawPromptRendered: true })
        } else {
            if (drawType === 'poly') {
                this.drawInteraction = olUtils.makeDrawPolygonInteraction()
                this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
                this.props.er2Map.olmap.addInteraction(this.drawInteraction)
            } else if (this.drawInteraction) {
                this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
                this.drawInteraction = null
            }
            this.setState({ currentZone, drawType, drawPromptRendered: false })
        }
    }

    setZoneColor = zone => (color) => {
        this.props.onSetZoneColor(zone, color.hex)
        if (this.state.currentZone !== zone) {
            this.setCurrentZone(zone)
        }
    }

    renderNotifications() {
        function getDrawPromptMessage(msgType, zone) {
            switch (msgType) {
            case 'poly':
                return `Click to begin drawing the ${zone} polygon on the map`
            case 'brush':
                return `Drag the mouse across the map to designate the ${zone} areas`
            default:
                return 'Unknown message type'
            }
        }

        const drawMessage = this.state.drawType ? getDrawPromptMessage(this.state.drawType, this.state.currentZone) : ''
        return (<div>
            <Snackbar
                open={this.state.drawPromptRendered === false}
                autoHideDuration={6000}
                onClose={() => this.setState({ drawPromptRendered: true })}
                message={<span id="message-id">{drawMessage}</span>}
            />
        </div>)
    }

    renderMapbrusher() {
        // Render canvas over catena map
        const mapEl = document.getElementById('map')
        const rect = mapEl.getBoundingClientRect()
        return (
            <Mapbrusher
                ref={mapbrusher => (this.mapbrusher = mapbrusher)}
                color={this.props.transims.zoneColors[this.state.currentZone]}
                hasAnalysisPanel={this.props.hasAnalysisPanel}
                hasHeader={this.props.hasHeader}
                height={rect.height}
                inactive={this.state.drawType === 'poly' || !this.state.currentZone}
                onSaveLine={this.onBrushUpdated}
                theme={this.props.theme}
                tocVisible={this.props.tocVisible}
                width={rect.width}
            />
        )
    }

    render() {
        const { classes } = this.props
        const zones = ['Electricity', 'Fuel', 'Transportation', 'Water']
        const radioStyle = {
            marginRight: '-10px',
            color: colors.secondaryTwo,
        }
        const start = this.state.startTime

        return (
            <div className={this.props.theme.controlBar}>
                <div className={this.props.theme.ficusControlsHeader}>
                    <span className={this.props.theme.ficusControlsHeaderTitle} style={{ display: 'inline-block', width: 270 }}>Zone Types</span>
                    <span
                        className={`fa fa-question-circle-o ${this.props.theme.helpIcon}`}
                        onClick={() => this.setState({ helpGeneral: !this.state.helpGeneral })}
                        role={'button'}
                        tabIndex={-1}
                    />
                    {this.state.helpGeneral && (
                        <div className={this.props.theme.help} style={{ marginRight: 20 }}>Select a zone type and draw using the polygon tool or the air brush.</div>
                    )}
                </div>
                {zones.map(z => (
                    <Fragment key={z}>
                        <div>
                            <Button
                                className={this.state.currentZone === z ? classes.buttonSelected : classes.button}
                                onClick={this.setCurrentZone(z)}
                                variant={'contained'}
                            >{z}</Button>
                            <Checkbox
                                checked={!this.state.hidden[z]}
                                color={'primary'}
                                onClick={this.onToggleVisible(z)}
                            />
                            <IconButton
                                onClick={this.onTrash(z)}
                                style={{ fontSize: '1.2em' }}
                            >
                                <span><i className="material-icons">delete</i></span>
                            </IconButton>
                            <Color
                                color={this.props.transims.zoneColors[z].replace(opacity, '0.7')}
                                onSel={this.setZoneColor(z)}
                            />
                        </div>
                    </Fragment>
                ))}
                <div className={this.props.theme.ficusControlsHeader}>
                    <span className={this.props.theme.ficusControlsHeaderTitle} style={{ display: 'inline-block', width: 270 }}>Drawing Method</span>
                    <span
                        className={`fa fa-question-circle-o ${this.props.theme.helpIcon}`}
                        onClick={() => this.setState({ helpDrawMethod: !this.state.helpDrawMethod })}
                        role={'button'}
                        tabIndex={-1}
                    />
                    {this.state.helpDrawMethod && (
                        <div className={this.props.theme.help} style={{ marginRight: 20 }}>Draw using the polygon tool or the air brush.</div>
                    )}
                </div>
                <div className={classes.container}>
                    <RadioGroup
                        aria-label="draw"
                        name="draw"
                        className={classes.radioGroup}
                        value={this.state.drawType}
                        onChange={this.setDrawType}
                    >
                        <FormControlLabel
                            className={classes.radioLabel}
                            value="poly"
                            control={<Radio style={radioStyle} />}
                            label="Polygon"
                            labelPlacement={'end'}
                        />
                        <FormControlLabel
                            value="brush"
                            control={<Radio style={radioStyle} />}
                            label="Air Brush"
                            labelPlacement="end"
                        />
                    </RadioGroup>
                </div>
                <div style={{ marginTop: 30, textAlign: 'center' }}>
                    <Button
                        disabled={this.props.transims.running}
                        onClick={this.onRun}
                        variant={'contained'}
                    >Run</Button>
                </div>
                {this.props.transims.running && (
                    <div>
                        <div className={classes.startTime}>
                            <span style={{ display: 'inline-block', width: 24, height: 24 }}>
                                <i className="fa fa-spinner fa-spin" />
                            </span>
                            Started on {`${start.format('MM/DD/YYYY')} at ${start.format('hh:mm:ss a')}`}
                        </div>
                    </div>
                )}
                {this.state.drawType === 'brush' && this.state.currentZone && this.renderMapbrusher()}
                {this.renderNotifications()}
            </div>
        )
    }
}

export default withStyles(styles)(Transims)
